import {
  TrackingPropsInterface,
  TrackingProps,
  IMetaData,
  ITargetContent,
} from '../organisms/MerchandisingHeroBanner/MerchandisingHeroBanner.types';
import { MangoAssests } from '../organisms/MangoPilot/MangoPilot.types';
import { canUseDOM } from '@marriott/shared/mi-helper-utils';
import { getEVAR48, getSPID } from '@marriott/mi-headless-utils';

export const getTrackingPropertiesDynamic = (
  trackingProperties: TrackingProps,
  metaData: IMetaData,
  targetData: ITargetContent,
  isAuth: boolean,
  isTargetContentError: boolean,
  mboxParameter = ''
): TrackingPropsInterface => {
  /**
   *
   * @returns it returns the value of atCCeVar48
   * @description this fucntion returns value of atCCeVar48, conditionally,
   *
   */
  const getAtCCevar48 = () => {
    const isCobrand =
      isTargetContentError || !mboxParameter
        ? trackingProperties?.merchandisingCategory === 'cobrandNonPointsMath'
        : metaData?.type === 'CoBrand';

    if (canUseDOM && isCobrand) {
      // If type in MBOP response is cobrand then construct evar48
      if (isTargetContentError || !mboxParameter) return trackingProperties?.atCCeVar48;
      else return getEVAR48(targetData);
    }
    // if type in MBOP response is non cobrand then pass empty in evar48
    return '';
  };

  return {
    trackingContentPosition: metaData
      ? mboxParameter?.replace(/-mbox$/, '')
      : trackingProperties?.trackingContentPosition,
    trackingDescription: metaData ? 'dca' : trackingProperties?.trackingDescription,
    trackingOfferType: metaData ? 'dca' : trackingProperties?.trackingOfferType,
    trackingTag: metaData ? String(metaData?.merTag) : trackingProperties?.trackingTag1,
    additionalTrackingVariables: metaData
      ? metaData?.optionalFields
        ? 'true'
        : 'false'
      : trackingProperties?.atCCeVar48,
    atCCeVar48: getAtCCevar48(),
    clickTrack: trackingProperties?.clickTrack ?? true,
    impressionTrack: trackingProperties?.impressionTrack ?? true,
    impressionCount: trackingProperties?.impressionCount ?? true,
    isAuth: isAuth,
    merchandisingCategory: metaData ? metaData?.type : trackingProperties?.merchandisingCategory,
    isDCAEnabled: true,
    // only pick when we have UXL response
    spid: getSPID({ metaData: metaData }),
    rpcCode: metaData?.rpcCode || '',
    cell: metaData?.cell || '',
    offerCode: metaData?.offerCode || '',
    type: metaData?.type || '',
    clickTrackValSeparator: ';',
  };
};

/**
 *
 * @returns it returns the AEM tracking properties
 * @param trackingProperties it refers to the AEM tracking properties
 * @param isAuth it refers to auth
 * @description this fucntion returns value of AEM tracking properties without depending on UXL response.
 *
 */
export const getAEMTrackingProperties = (
  trackingProperties: TrackingProps,
  isAuth: boolean
): TrackingPropsInterface => {
  return {
    trackingContentPosition: trackingProperties?.trackingContentPosition,
    trackingDescription: trackingProperties?.trackingDescription,
    trackingOfferType: trackingProperties?.trackingOfferType,
    trackingTag: trackingProperties?.trackingTag1,
    additionalTrackingVariables: 'false',
    atCCeVar48: trackingProperties?.atCCeVar48 ?? '',
    clickTrack: trackingProperties?.clickTrack ?? true,
    impressionTrack: trackingProperties?.impressionTrack ?? true,
    impressionCount: trackingProperties?.impressionCount ?? true,
    isAuth: isAuth,
    merchandisingCategory: trackingProperties?.merchandisingCategory,
    isDCAEnabled: true,
    spid: '',
    rpcCode: '',
    cell: '',
    offerCode: '',
    type: '',
    clickTrackValSeparator: ';',
  };
};

//Creating rendtions for koddi response
export const mangoDynamicMediaTransformation = (mangoAssets: MangoAssests) => {
  const getMangoRenditions = () => {
    const mangoRenditions = [];
    mangoRenditions.push({
      renditionPath: mangoAssets?.primaryImage_desktop,
      smartCropType: null,
      damPath: '',
      dynamic: true,
      height: 314,
      mediaQuery: 'min-width',
      mediaValue: '768px',
      width: 558,
    });
    mangoRenditions.push({
      renditionPath: mangoAssets?.primaryImage_mobile ?? mangoAssets?.primaryImage_desktop,
      smartCropType: null,
      damPath: '',
      dynamic: true,
      height: 314,
      mediaQuery: 'max-width',
      mediaValue: '767px',
      width: 558,
    });
    return mangoRenditions;
  };

  const renditions = mangoAssets // Check if mangoAssets is defined
    ? getMangoRenditions()
    : []; // If mangoAssets is not defined, return an empty array

  const aemDynamicJSON = {
    altText: mangoAssets?.altText,
    damPath: '',
    assetPath: mangoAssets?.primaryImage_mobile ?? mangoAssets?.primaryImage_desktop,
    renditions: renditions,
  };
  return aemDynamicJSON;
};

export const isExternalLink = (linkHref: string, openInaNewTab: boolean) => {
  try {
    if (canUseDOM) {
      const currentDomain = window?.location?.hostname;
      const urlDomain = new URL(linkHref)?.hostname;
      return currentDomain !== urlDomain;
    } else {
      return openInaNewTab === true; // check for evergreen content on server side
    }
  } catch {
    return false;
  }
};

export const returnDecodedString = (input: string): string => {
  return input?.replaceAll('&amp;', '&') ?? '';
};

export const getDirection = () => {
  if (typeof document !== 'undefined') {
    const directionValue = document?.documentElement.getAttribute('dir');
    let direction: 'ltr' | 'rtl' | undefined;

    if (directionValue === 'ltr' || directionValue === 'rtl') {
      direction = directionValue;
    } else {
      direction = 'ltr';
    }
    return direction;
  } else {
    return 'ltr';
  }
};

export const removeParentTagRTE = (textRTE: string): string => {
  return textRTE.substring(textRTE.indexOf('>') + 1, textRTE.lastIndexOf('<')) || textRTE;
};

export enum BadgeVariation {
  'Inline' = 'inline',
  'Overlay' = 'overlay',
}

export enum CAROUSEL {
  ID = 'merchandisingCarousel',
  ITEM = 'carousel__item',
  EVENT = 'updateCarousel',
  LOADER = 'skeleton-loader-composite',
}

/**
 *
 * @param ref it refers to the component-Ref authored inside carousel
 * @param isComponentNeeded if its false means, this component has to be removed from carousel
 * @description this method has to be called from carousel item to remove the skeleton loader
 */
export const updateCarousel = (ref: React.RefObject<HTMLElement>, isComponentNeeded = true) => {
  if (ref?.current) {
    const carouselItemNode = ref.current.closest('li.carousel__item');
    const carousel = ref.current?.closest(`#${CAROUSEL.ID}`);
    if (carouselItemNode && carousel) {
      const updatedCount = Number(carousel.getAttribute('data-count')) + 1;
      const maxCount = Number(carousel.getAttribute('data-total') || 0);
      carousel.setAttribute('data-count', String(updatedCount));
      if (isComponentNeeded === false) {
        carouselItemNode.remove();
      }
      if (updatedCount >= maxCount) {
        carousel?.querySelectorAll(`li.${CAROUSEL.ITEM}`)?.forEach(item => {
          item?.classList?.remove(CAROUSEL.LOADER);
        });
        carousel.setAttribute('data-carousel-updated', 'true');
        carousel?.dispatchEvent(new CustomEvent(CAROUSEL.EVENT));
      }
    } else {
      console.info('Element not authored inside carousel');
    }
  } else {
    console.error('while updating carousel either you called at server side or ref is not yet rendered');
  }
};
